<template>
  <div class="pre-projects-sample-preview">
    <div class="pre-projects-sample-preview__header">
      <div class="pre-projects-sample-preview__header-content">
        <h1 class="pre-projects-sample-preview__header-title">{{ $t('page.pre-projects.title') }}</h1>
      </div>
    </div>

    <div class="pre-projects-sample-preview__main">
      <div class="pre-projects-sample-preview__filters-panel">
        <div class="pre-projects-sample-preview__tabs" role="tablist">
          <span
            v-for="tab in tabs"
            :key="tab.key"
            class="pre-projects-sample-preview__tab"
            :class="{ 'is-active': tab.key === 'all' }"
            role="tab"
          >
            <BaseIcon :name="tab.icon" class="pre-projects-sample-preview__tab-ico" />
            <span>{{ tab.label }}</span>
          </span>
        </div>
      </div>

      <div class="pre-projects-sample-preview__table-wrap">
        <div class="pre-projects-sample-preview__results">
          <span class="pre-projects-sample-preview__count">
            <span class="number-monospace">{{ previewProjects.length }}</span>
            {{ ' ' + $tc('page.pre-projects.results', previewProjects.length) }}
          </span>
        </div>

        <table class="pre-projects-sample-preview__table">
          <thead>
            <tr>
              <th>{{ $t('page.pre-projects.data-table-titles.title') }}</th>
              <th>{{ $t('page.pre-projects.data-table-titles.emitter') }}</th>
              <th>{{ $t('page.pre-projects.data-table-titles.location') }}</th>
              <th v-for="year in budgetYears" :key="year" class="pre-projects-sample-preview__budget-col">
                {{ $t('page.pre-projects.data-table-titles.budget', { year }) }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in previewProjects" :key="item.id">
              <td>
                <div class="pre-projects-sample-preview__row-title">{{ item.title }}</div>
                <div class="pre-projects-sample-preview__row-subtitle">
                  <span v-if="item.projectNumber" class="pre-projects-sample-preview__pill"
                    >#{{ item.projectNumber }}</span
                  >
                </div>
              </td>
              <td>{{ item.emitter }}</td>
              <td>
                <div class="pre-projects-sample-preview__city-name">{{ item.cityName }}</div>
                <span v-if="item.regionName" class="pre-projects-sample-preview__pill">{{ item.regionName }}</span>
              </td>
              <td
                v-for="year in budgetYears"
                :key="`${item.id}-${year}`"
                class="pre-projects-sample-preview__budget-col"
              >
                <span class="pre-projects-sample-preview__budget-amount">
                  {{ budgetLabel(item, year) }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import BaseIcon from '@/components/base/BaseIcon.vue';
import { formatNumToCurrency } from '@/util/currency-util';

const BUDGET_YEARS = Object.freeze([2026, 2027, 2028]);

const PREVIEW_PROJECTS = Object.freeze([
  {
    id: 'preview-1',
    title: 'Réfection de l’aqueduc — 8e Avenue',
    projectNumber: '2026-INF-0142',
    emitter: 'Ville de Montréal',
    cityName: 'Montréal',
    regionName: 'Montréal',
    budgets: { 2026: 1850000, 2027: 920000, 2028: null },
  },
  {
    id: 'preview-2',
    title: 'Construction d’un garage municipal — secteur est',
    projectNumber: '2026-BAT-0087',
    emitter: 'Ville de Saint-Jérôme',
    cityName: 'Saint-Jérôme',
    regionName: 'Laurentides',
    budgets: { 2026: 4200000, 2027: 3100000, 2028: 800000 },
  },
  {
    id: 'preview-3',
    title: 'Travaux d’éclairage public — artères principales',
    projectNumber: '2027-ELE-0031',
    emitter: 'Ville de Lévis',
    cityName: 'Lévis',
    regionName: 'Chaudière-Appalaches',
    budgets: { 2026: null, 2027: 1450000, 2028: 650000 },
  },
  {
    id: 'preview-4',
    title: 'Rénovation des vestiaires — complexe sportif municipal',
    projectNumber: '2026-SPO-0055',
    emitter: 'Ville de Saguenay',
    cityName: 'Saguenay',
    regionName: 'Saguenay–Lac-Saint-Jean',
    budgets: { 2026: 780000, 2027: null, 2028: null },
  },
  {
    id: 'preview-5',
    title: 'Remplacement de conduites d’égout — quartier centre',
    projectNumber: '2026-INF-0218',
    emitter: 'Ville de Québec',
    cityName: 'Québec',
    regionName: 'Capitale-Nationale',
    budgets: { 2026: 2600000, 2027: 1750000, 2028: 400000 },
  },
  {
    id: 'preview-6',
    title: 'Aménagement d’un parc linéaire — berges de la rivière',
    projectNumber: '2027-PAR-0012',
    emitter: 'Ville de Gatineau',
    cityName: 'Gatineau',
    regionName: 'Outaouais',
    budgets: { 2026: null, 2027: 980000, 2028: 1200000 },
  },
  {
    id: 'preview-7',
    title: 'Réfection de chaussée — boulevard principal',
    projectNumber: '2026-ROUT-0099',
    emitter: 'Ville de Sherbrooke',
    cityName: 'Sherbrooke',
    regionName: 'Estrie',
    budgets: { 2026: 3200000, 2027: 1500000, 2028: null },
  },
  {
    id: 'preview-8',
    title: 'Modernisation de la station de traitement d’eau',
    projectNumber: '2026-EAU-0044',
    emitter: 'SQI',
    cityName: 'Trois-Rivières',
    regionName: 'Mauricie',
    budgets: { 2026: 5600000, 2027: 4800000, 2028: 2100000 },
  },
]);

export default {
  name: 'PreProjectsSamplePreview',
  components: { BaseIcon },
  data() {
    return {
      budgetYears: BUDGET_YEARS,
      previewProjects: PREVIEW_PROJECTS,
    };
  },
  computed: {
    tabs() {
      return [
        { key: 'all', icon: 'list', label: this.$t('page.pre-projects.views.all') },
        { key: 'infra', icon: 'user', label: 'Infrastructures 2026' },
        { key: 'saved', icon: 'bookmark', label: this.$t('views.default.followed') },
      ];
    },
  },
  methods: {
    budgetLabel(item, year) {
      const amount = item.budgets && item.budgets[year];
      if (amount == null) return '—';
      return formatNumToCurrency(amount, 0);
    },
  },
};
</script>

<style lang="scss" scoped>
.pre-projects-sample-preview {
  display: flex;
  flex-direction: column;
  height: calc(var(--viewport-height) - #{$menu-top-bar-height});
  max-height: calc(var(--viewport-height) - #{$menu-top-bar-height});
  min-height: 0;
  overflow: hidden;
  background: $color-slate-50;

  &__header {
    align-items: stretch;
    background: $color-white;
    box-shadow: $shadow-dropdown;
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    height: $header-height;
  }

  &__header-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    min-width: 0;
    padding: 16px 32px;
  }

  &__header-title {
    color: $color-normal;
    display: flex;
    font-family: $font-family-raleway;
    font-size: $font-size-page-title;
    font-weight: normal;
    margin: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    text-transform: uppercase;
    white-space: nowrap;
  }

  &__main {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-height: 0;
    overflow: hidden;
    padding: 24px 32px 32px;
  }

  &__filters-panel {
    background: $color-white;
    box-shadow: $shadow-dropdown;
    flex-shrink: 0;
    margin: -24px -32px 16px;
    padding: 10px 32px 4px;
  }

  &__tabs {
    display: flex;
    gap: 4px;
    overflow: hidden;
    padding-bottom: 6px;
  }

  &__tab {
    align-items: center;
    border-radius: 8px;
    color: $color-slate-600;
    display: inline-flex;
    font-size: 13px;
    font-weight: 600;
    gap: 6px;
    padding: 8px 12px;
    white-space: nowrap;

    &.is-active {
      background: $color-accent-100;
      color: $color-accent;
    }
  }

  &__tab-ico {
    width: 14px;
    height: 14px;
  }

  &__table-wrap {
    flex: 1;
    min-height: 0;
    overflow: hidden;
  }

  &__results {
    margin-bottom: 8px;
  }

  &__count {
    color: $color-neutral;
    font-size: $font-size-small;
    padding: 4px 12px;
    text-transform: uppercase;
  }

  &__table {
    background: $color-white;
    border-collapse: separate;
    border-radius: $border-radius-root;
    width: 100%;

    th,
    td {
      border-bottom: 0.5px solid $color-border;
      font-size: 13px;
      padding: 12px 14px;
      text-align: left;
      vertical-align: top;
    }

    th {
      color: $color-slate-600;
      font-weight: 600;
      white-space: nowrap;
    }

    td {
      color: $color-normal;
    }
  }

  &__budget-col {
    text-align: right !important;
    white-space: nowrap;
  }

  &__row-title {
    color: $color-normal;
    font-weight: 600;
  }

  &__row-subtitle {
    margin-top: 4px;
  }

  &__pill {
    background: $color-slate-100;
    border-radius: 4px;
    color: $color-slate-600;
    display: inline-block;
    font-size: 11px;
    font-weight: 600;
    margin-top: 4px;
    padding: 2px 6px;
  }

  &__city-name {
    font-weight: 600;
  }

  &__budget-amount {
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }
}
</style>
